import { Box, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import PersonalChannel from "../PersonalChannel";
import { useLocation, useSearchParams } from "react-router-dom";
import PageRoutes, { INSIDER_PLAN_ID } from "../../utils/constants";
import MyNAHLTV from "../MyNAHLTV";
import MyClips from "./MyClips";
import DownloadClipsList from "./DownloadClipsList";
import useCommonDetails from "../../Common/Hooks/useCommonDetails";
import useConfig from "../../hooks/useConfig";
import {
  isSpotlightViewPath,
  isSpotlightWizardPath,
} from "../../utils/spotlightPaths";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const DownloadTabs = ({ children, subscriptionDetails }: any) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tab_id: string | null = searchParams?.get("tab_id");

  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    searchParams.set("tab_id", newValue.toString());
    setSearchParams(searchParams);
  };
  const userDetails = useCommonDetails();
  const location = useLocation();
  const configData = useConfig();
  /*
   * THE STUDIO ALREADY SERVES THESE TWO TABS. On a tenant with the Audience
   * Layer switched on, the v2 Studio shows Spotlights, Clips and Downloads read
   * from the layer, and the copies here read the tenant's own tables, so
   * leaving both up gives two different answers to the same question.
   *
   * The Clips tab is the one that matters. Its download calls
   * creteDownloadVideo, which spends a ClipsCredit and writes only
   * UserVideoClip.Clips, and registers nothing with the layer. So on an enabled
   * tenant, where the layer is meant to be the single record of every
   * download, that tab is a charge nothing records.
   *
   * DROPPED ONLY WHEN SOMETHING IS LEFT TO SHOW, which is why this reads
   * REACT_APP_FEATURE as well as the layer flag. That variable is false in
   * eight of the thirteen tenant env files (bbsn, dexter, hbc, lvictv,
   * golivesports, lakeplacid, olatv, usalacrossetv), and on those the Channels
   * tab is hidden AND its panel is not rendered. Gating on the layer flag alone
   * did not avoid the blank page, it only postponed it to the moment somebody
   * flips that flag, which is the single operation this whole feature is built
   * around: tabs 1 and 2 would go while tab 0 was already not rendering,
   * leaving a paying subscriber a tab strip holding one display:none tab and
   * nothing underneath it.
   *
   * So a tenant that hides Channels keeps Clips and Downloads whatever else is
   * switched on. Giving those tenants the Studio here instead is the better end
   * state and is a larger change than this one.
   */
  const channelsPanelRenders = process.env.REACT_APP_FEATURE === "true";
  const studioOwnsClipsAndDownloads =
    configData?.audienceLayerEnabled === true && channelsPanelRenders;
  useEffect(() => {
    // Clamp to what is actually rendered: with the Studio owning tabs 1 and 2,
    // a stale ?tab_id=2 would otherwise select a tab that is not there and show
    // an empty page.
    const highestTab = studioOwnsClipsAndDownloads ? 0 : 2;
    const requested = tab_id === null ? NaN : parseInt(tab_id);
    setValue(
      Number.isNaN(requested) || requested < 0 || requested > highestTab
        ? 0
        : requested
    );
  }, [tab_id, studioOwnsClipsAndDownloads]);
  // The spotlight build wizard (Season/Teams/Players/Actions and its sibling
  // steps) brings its own Studio-styled header, so the Spotlights/Clips/
  // Downloads tab strip above it would be redundant chrome. The finished
  // spotlight view at /spotlight/:id now has its own v2 header (with its own
  // spotlight switcher) for the same reason, so it hides the strip too.
  const isChannelWizard = isSpotlightWizardPath(location.pathname);
  const isPersonalChannelView = isSpotlightViewPath(location.pathname);

  return (
    <Box>
      <Box sx={{ width: "100%", p: { xs: 0, md: 4 } }}>
        {!isChannelWizard && !isPersonalChannelView && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab
                sx={{
                  "&.MuiTab-root": {
                    display:
                      process.env.REACT_APP_FEATURE !== "true" ? "none" : "flex",
                  },
                }}
                label="Channels"
                {...a11yProps(0)}
              />

              {!studioOwnsClipsAndDownloads && (
                <Tab label="Clips" {...a11yProps(1)} />
              )}

              {!studioOwnsClipsAndDownloads && (
                <Tab label="Downloads" {...a11yProps(2)} />
              )}
            </Tabs>
          </Box>
        )}
        {process.env.REACT_APP_FEATURE == "true" && (
          <CustomTabPanel value={value} index={0}>
            {isChannelWizard ? (
              <MyNAHLTV />
            ) : (
              <PersonalChannel subscriptionDetails={subscriptionDetails} />
            )}
          </CustomTabPanel>
        )}

        {!studioOwnsClipsAndDownloads && (
          <CustomTabPanel value={value} index={1}>
            <MyClips
              isInsider={
                subscriptionDetails?.plan_type_id == INSIDER_PLAN_ID ||
                userDetails?.UserRole?.Permissions?.includes("download")
              }
            />
          </CustomTabPanel>
        )}

        {!studioOwnsClipsAndDownloads && (
          <CustomTabPanel value={value} index={2}>
            <DownloadClipsList
              isInsider={
                subscriptionDetails?.plan_type_id == INSIDER_PLAN_ID ||
                userDetails?.UserRole?.Permissions?.includes("download")
              }
            />
          </CustomTabPanel>
        )}
      </Box>
      {children}
    </Box>
  );
};
export default DownloadTabs;
